import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
axios.defaults.withCredentials = true;
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FilmSlate, Play, SpeakerHigh, CheckCircle, Sparkle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = [
  { id: "facebook_ad", label: "Facebook Ad", sub: "1:1 · feed-ready" },
  { id: "instagram_reel", label: "Instagram Reel", sub: "9:16 · vertical" },
  { id: "product_commercial", label: "Product Commercial", sub: "16:9 · cinematic" },
  { id: "business_intro", label: "Business Intro", sub: "16:9 · about us" },
  { id: "holiday_promo", label: "Holiday Promo", sub: "9:16 · seasonal" },
];

export default function AiVideoStudioPage() {
  const { activeId, activeClient } = useClient();
  const [vtype, setVtype] = useState("facebook_ad");
  const [topic, setTopic] = useState("");
  const [product, setProduct] = useState("");
  const [duration, setDuration] = useState(20);
  const [voice, setVoice] = useState("");
  const [plan, setPlan] = useState(null);
  const [job, setJob] = useState(null);
  const [previewing, setPreviewing] = useState("");

  const { data: voices = [] } = useQuery({ queryKey: ["video-voices"], queryFn: () => axios.get(`${API}/api/video/voices`).then((r) => r.data) });
  const { data: videos = [] } = useQuery({ queryKey: ["videos", activeId], enabled: !!activeId,
    queryFn: () => axios.get(`${API}/api/video/list?client_id=${activeId}`).then((r) => r.data), refetchInterval: 15000 });
  useEffect(() => { setVoice(activeClient?.video_voice || "onyx"); }, [activeClient]);

  const genPlan = useMutation({
    mutationFn: () => axios.post(`${API}/api/video/plan`, { client_id: activeId, video_type: vtype, topic, product_title: product, duration: Number(duration), voice }).then((r) => r.data),
    onSuccess: (d) => { setPlan(d); setJob(null); toast.success("Video plan ready — review it, then render"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Plan generation failed"),
  });
  const render = useMutation({
    mutationFn: () => axios.post(`${API}/api/video/render`, { plan_id: plan.id, voice }).then((r) => r.data),
    onSuccess: (d) => { setJob({ id: d.job_id, status: "rendering", step: "starting" }); poll(d.job_id); },
    onError: (e) => toast.error(e.response?.data?.detail || "Render failed to start"),
  });

  const poll = (jid) => {
    const t = setInterval(async () => {
      try {
        const j = (await axios.get(`${API}/api/video/status/${jid}`)).data;
        setJob(j);
        if (j.status === "done") { clearInterval(t); toast.success("Video rendered — sent to Approval Queue"); }
        if (j.status === "failed") { clearInterval(t); toast.error(`Render failed: ${j.error || "unknown"}`); }
      } catch { clearInterval(t); }
    }, 4000);
  };

  const previewVoice = async (v) => {
    setPreviewing(v);
    try {
      const r = await axios.post(`${API}/api/video/voice-preview`, { voice: v });
      new Audio(`${API}${r.data.audio_url}`).play();
    } catch { toast.error("Preview failed"); }
    setTimeout(() => setPreviewing(""), 2500);
  };
  const saveDefaultVoice = async () => {
    await axios.put(`${API}/api/clients/${activeId}`, { video_voice: voice });
    toast.success(`${voice} is now this workspace's default voice`);
  };

  return (
    <div data-testid="video-studio-page" className="space-y-6">
      <div>
        <h1 className="font-head text-2xl text-white flex items-center gap-2"><FilmSlate className="brand-text" weight="fill" /> AI Video Studio</h1>
        <p className="text-sm text-[#8a8a8a] mt-1">Complete marketing videos — hook, script, scenes, voiceover, captions, music, and CTA — generated from your Brand Kit. Every video routes through the Approval Queue.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-matte rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <button key={t.id} data-testid={`vtype-${t.id}`} onClick={() => setVtype(t.id)}
                className={`text-left rounded-lg border p-3 transition-colors ${vtype === t.id ? "border-[#6B3DFF] bg-[#6B3DFF]/10" : "border-white/10 bg-[#10101A] hover:border-white/25"}`}>
                <div className="text-sm text-white font-medium">{t.label}</div>
                <div className="text-[11px] text-[#7a7a7a]">{t.sub}</div>
              </button>
            ))}
          </div>
          <div><Label className="label-caps">Topic / campaign</Label>
            <Input data-testid="video-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Summer sale — 20% off all blends" className="bg-[#10101A] border-white/10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Featured product (optional)</Label>
              <Input data-testid="video-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Product name" className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div><Label className="label-caps">Length</Label>
              <div className="flex gap-2 mt-1">{[15, 20, 30].map((d) => (
                <button key={d} data-testid={`video-dur-${d}`} onClick={() => setDuration(d)}
                  className={`px-3 py-2 rounded-md text-sm border ${duration === d ? "border-[#6B3DFF] text-white bg-[#6B3DFF]/10" : "border-white/10 text-[#9a9a9a]"}`}>{d}s</button>))}
              </div></div>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="label-caps">Voiceover voice</Label>
              <button data-testid="save-default-voice" onClick={saveDefaultVoice} className="text-[11px] text-[#6B3DFF] hover:underline">Set as workspace default</button></div>
            <div className="grid sm:grid-cols-2 gap-2 mt-1">
              {voices.map((v) => (
                <div key={v.id} data-testid={`voice-${v.id}`} onClick={() => setVoice(v.id)}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 cursor-pointer ${voice === v.id ? "border-[#6B3DFF] bg-[#6B3DFF]/10" : "border-white/10 bg-[#10101A]"}`}>
                  <div><div className="text-sm text-white">{v.name} <span className="text-[10px] text-[#7a7a7a]">{v.gender}</span></div>
                    <div className="text-[10px] text-[#7a7a7a]">{v.tone}</div></div>
                  <button data-testid={`voice-preview-${v.id}`} onClick={(e) => { e.stopPropagation(); previewVoice(v.id); }}
                    className="text-[#9a9a9a] hover:text-white" aria-label={`Preview ${v.name}`}>
                    <SpeakerHigh size={16} weight={previewing === v.id ? "fill" : "regular"} className={previewing === v.id ? "brand-text" : ""} /></button>
                </div>))}
            </div>
          </div>
          <Button data-testid="generate-plan-btn" disabled={genPlan.isPending || !activeId} onClick={() => genPlan.mutate()}
            className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
            <Sparkle className="mr-2" weight="fill" /> {genPlan.isPending ? "Directing your video…" : "Generate Video Plan"}</Button>
        </div>

        <div className="card-matte rounded-xl p-5">
          {!plan && !job && <div className="h-full flex items-center justify-center text-sm text-[#6a6a6a]" data-testid="video-empty">Your AI video plan will appear here.</div>}
          {plan && !job && (
            <div className="space-y-3" data-testid="video-plan">
              <div className="text-[11px] uppercase tracking-wider text-[#6B3DFF] font-semibold">Hook</div>
              <p className="text-white text-sm">{plan.plan.hook}</p>
              <div className="text-[11px] uppercase tracking-wider text-[#6B3DFF] font-semibold">Scenes</div>
              <ol className="space-y-2">{(plan.plan.scenes || []).map((s, i) => (
                <li key={i} className="rounded-lg bg-[#10101A] border border-white/10 p-3">
                  <div className="text-xs text-[#9a9a9a]">{i + 1}. {s.visual}</div>
                  <div className="text-sm text-white mt-1">"{s.voiceover}"</div>
                  {s.caption && <div className="text-[11px] brand-text mt-1">Caption: {s.caption}</div>}
                </li>))}
              </ol>
              <div className="text-xs text-[#9a9a9a]">CTA: <span className="text-white">{plan.plan.cta}</span> · SEO: <span className="text-white">{plan.plan.seo_title}</span></div>
              <div className="text-xs text-[#9a9a9a]">{(plan.plan.hashtags || []).map((h) => `#${String(h).replace(/^#/, "")}`).join(" ")}</div>
              <Button data-testid="render-video-btn" disabled={render.isPending} onClick={() => render.mutate()} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <Play className="mr-2" weight="fill" /> Render Video ({duration}s · {voice})</Button>
            </div>)}
          {job && job.status === "rendering" && (
            <div className="h-full flex flex-col items-center justify-center gap-3" data-testid="video-rendering">
              <div className="animate-spin h-8 w-8 border-2 border-[#6B3DFF] border-t-transparent rounded-full" />
              <div className="text-sm text-white">Rendering… <span className="text-[#9a9a9a]">({job.step})</span></div>
              <div className="text-[11px] text-[#6a6a6a]">Voiceover, scenes, captions, music, end-card — 1-3 minutes</div>
            </div>)}
          {job && job.status === "done" && (
            <div className="space-y-3" data-testid="video-done">
              <video data-testid="video-player" controls src={`${API}${job.video_path}`} className="w-full rounded-lg border border-white/10 max-h-[420px] bg-black" />
              <div className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle weight="fill" /> Sent to Approval Queue — approve to schedule/publish</div>
              <a data-testid="video-download" href={`${API}${job.video_path}`} download className="text-sm text-[#6B3DFF] hover:underline">Download MP4</a>
            </div>)}
          {job && job.status === "failed" && (
            <div className="space-y-3" data-testid="video-failed">
              <div className="text-sm text-red-400">Render failed: {job.error}</div>
              <Button data-testid="retry-render-btn" disabled={render.isPending} onClick={() => render.mutate()} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <Play className="mr-2" weight="fill" /> Retry Render</Button>
            </div>)}
        </div>
      </div>

      {videos.length > 0 && (
        <div className="card-matte rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3">Recent videos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {videos.map((v) => (
              <a key={v.id} data-testid={`video-item-${v.id}`} href={`${API}${v.video_path}`} target="_blank" rel="noreferrer" className="block group">
                <img src={`${API}${v.thumb_path}`} alt={v.seo_title || v.video_type} className="rounded-lg border border-white/10 w-full aspect-square object-cover group-hover:border-[#6B3DFF]/60" />
                <div className="text-[11px] text-[#9a9a9a] mt-1 truncate">{v.seo_title || v.video_type}</div>
              </a>))}
          </div>
        </div>)}
    </div>
  );
}
